import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import deliveryService, {
  DeliveryNote,
  MyDeliveryAssignment,
  CreateDeliveryPayload,
  CreateDeliveryFromWorkOrderPayload,
  CreateDeliveryFromWorkOrderBatchPayload,
  DeliveryListFilters,
  DeliveryPrintData,
  WorkOrderReadyForDelivery,
  DeliveryStatusTransitionBody,
  DeliveryTransitUpdateBody,
  DeliveryReadinessBoardFilters,
} from '@/services/delivery.service';

function invalidateAfterDeliveryTransition(queryClient: ReturnType<typeof useQueryClient>, data: DeliveryNote) {
  queryClient.invalidateQueries({ queryKey: ['deliveries'] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', 'my-assignments'] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', data.id] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', data.id, 'print'] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-work-orders'] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-lines'] });
  queryClient.invalidateQueries({ queryKey: ['deliveries', 'readiness-board'] });
  queryClient.invalidateQueries({ queryKey: ['work-orders'] });
  if (data.sales_order_id) {
    queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
  }
}

export function useDeliveries(filters?: DeliveryListFilters) {
  return useQuery({
    queryKey: ['deliveries', filters],
    queryFn: () => deliveryService.list(filters),
    staleTime: 30000,
  });
}

export function useDelivery(id: string | undefined) {
  return useQuery({
    queryKey: ['deliveries', id],
    queryFn: () => deliveryService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useMyDeliveryAssignments() {
  return useQuery<MyDeliveryAssignment[]>({
    queryKey: ['deliveries', 'my-assignments'],
    queryFn: () => deliveryService.listMyAssignments(),
    staleTime: 30000,
  });
}

export function useDeliveryPrintData(id: string | undefined) {
  return useQuery({
    queryKey: ['deliveries', id, 'print'],
    queryFn: () => deliveryService.getPrintData(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateDelivery() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateDeliveryPayload) => deliveryService.create(payload),
    onSuccess: (data: DeliveryNote) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-sales-orders-direct'] });
      message.success(`Delivery ${data.delivery_note_number} created successfully`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create delivery');
    },
  });
}

export function useCreateRetrospectiveDelivery() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (salesOrderId: string) => deliveryService.createRetrospective(salesOrderId),
    onSuccess: (data: DeliveryNote) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
      message.success(`Delivery note ${data.delivery_note_number} created. It will now appear in the Delivery list.`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create delivery note');
    },
  });
}

export function useDeliveryEligible(salesOrderId: string | undefined) {
  return useQuery({
    queryKey: ['delivery-eligible', salesOrderId],
    queryFn: () => deliveryService.isEligibleForDelivery(salesOrderId!),
    enabled: !!salesOrderId,
    staleTime: 60000,
  });
}

export function useDeliveryReadinessBoard(filters?: DeliveryReadinessBoardFilters) {
  return useQuery({
    queryKey: ['deliveries', 'readiness-board', filters],
    queryFn: () => deliveryService.getDeliveryReadinessBoard(filters),
    staleTime: 30000,
  });
}

export function useCreateDeliveryFromWorkOrderBatch() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateDeliveryFromWorkOrderBatchPayload) =>
      deliveryService.createFromWorkOrderBatch(payload),
    onSuccess: (data: DeliveryNote) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'readiness-board'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-lines'] });
      if (data.sales_order_id) {
        queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
      }
      message.success(`Delivery ${data.delivery_note_number} created successfully`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create delivery');
    },
  });
}

export function useEligibleWorkOrders(customerId?: string) {
  return useQuery({
    queryKey: ['deliveries', 'eligible-work-orders', customerId],
    queryFn: () => deliveryService.getEligibleWorkOrders(customerId),
    staleTime: 30000,
  });
}

export function useEligibleDeliveryLines(salesOrderId: string | undefined) {
  return useQuery({
    queryKey: ['deliveries', 'eligible-lines', salesOrderId],
    queryFn: () => deliveryService.getEligibleDeliveryLines(salesOrderId!),
    enabled: !!salesOrderId,
    staleTime: 30000,
  });
}

export function useEligibleSalesOrdersForDirect(customerId?: string) {
  return useQuery({
    queryKey: ['deliveries', 'eligible-sales-orders-direct', customerId],
    queryFn: () => deliveryService.getEligibleSalesOrdersForDirect(customerId),
    staleTime: 30000,
  });
}

export function useCreateDeliveryFromWorkOrder() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateDeliveryFromWorkOrderPayload) => deliveryService.createFromWorkOrder(payload),
    onSuccess: (data: DeliveryNote) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      if (data.sales_order_id) {
        queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
      }
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-lines'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'readiness-board'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-sales-orders-direct'] });
      message.success(`Delivery ${data.delivery_note_number} created successfully`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create delivery');
    },
  });
}

export function useCreateDirectDeliveryFromSalesOrder() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateDeliveryPayload) => deliveryService.create(payload),
    onSuccess: (data: DeliveryNote) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      if (data.sales_order_id) {
        queryClient.invalidateQueries({ queryKey: ['sales-orders', data.sales_order_id] });
      }
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-sales-orders-direct'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries', 'eligible-work-orders'] });
      message.success(`Delivery ${data.delivery_note_number} created successfully`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create delivery');
    },
  });
}

export function useUpdateDeliveryItemPacking(deliveryId: string | undefined) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (params: { itemId: string; body: { size?: string; weight?: number; weight_uom?: string; boxes?: number; hsn_code?: string } }) =>
      deliveryService.updateItemPacking(deliveryId!, params.itemId, params.body),
    onSuccess: () => {
      if (deliveryId) {
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId] });
      }
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      message.success('Packing updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update packing');
    },
  });
}

export function useUpdateDeliveryCountryOfOrigin(deliveryId: string | undefined) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (country_of_origin: string | null) =>
      deliveryService.updateCountryOfOrigin(deliveryId!, country_of_origin),
    onSuccess: () => {
      if (deliveryId) {
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId] });
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId, 'print'] });
      }
      message.success('Country of origin updated');
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to update country of origin'
      );
    },
  });
}

export function useUpdateDeliveryWarehouse(deliveryId: string | undefined) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (warehouse_id: string) => deliveryService.updateWarehouse(deliveryId!, warehouse_id),
    onSuccess: () => {
      if (deliveryId) {
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId] });
      }
      message.success('Warehouse updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update warehouse');
    },
  });
}

export function useReplaceDeliveryPackages(deliveryId: string | undefined) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (packages: import('@/services/delivery.service').DeliveryPackageInput[]) =>
      deliveryService.replacePackages(deliveryId!, packages),
    onSuccess: () => {
      if (deliveryId) {
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId] });
        queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId, 'print'] });
      }
      message.success('Packages updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update packages');
    },
  });
}

export function useMarkDeliveryInTransit() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (params: { id: string; body?: DeliveryStatusTransitionBody }) =>
      deliveryService.markInTransit(params.id, params.body),
    onSuccess: (data) => {
      invalidateAfterDeliveryTransition(queryClient, data);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      message.success('Delivery marked in transit');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    },
  });
}

export function useUpdateDeliveryTransitDetails() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (params: { id: string; body: DeliveryTransitUpdateBody }) =>
      deliveryService.updateTransitDetails(params.id, params.body),
    onSuccess: (data) => {
      invalidateAfterDeliveryTransition(queryClient, data);
      message.success('Transit details updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update transit details');
    },
  });
}

export function useMarkDeliveryDelivered() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (params: { id: string; body?: DeliveryStatusTransitionBody }) =>
      deliveryService.markDelivered(params.id, params.body),
    onSuccess: (data) => {
      invalidateAfterDeliveryTransition(queryClient, data);
      message.success('Delivery marked complete');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    },
  });
}

export function useMarkDeliveryReturned() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (params: { id: string; body?: DeliveryStatusTransitionBody }) =>
      deliveryService.markReturned(params.id, params.body),
    onSuccess: (data) => {
      invalidateAfterDeliveryTransition(queryClient, data);
      message.success('Delivery marked returned');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    },
  });
}
